import { useState } from "react";
import { X, MapPin, Scissors, ArrowUpRight } from "lucide-react";

const PROJECTS = [
  {
    id: "p1",
    service: "Full Yard Mow",
    city: "New Braunfels, TX",
    img: "https://images.pexels.com/photos/7031593/pexels-photo-7031593.jpeg?auto=compress&cs=tinysrgb&w=1400",
    aspect: "tall",
  },
  {
    id: "p2",
    service: "Hedge & Edge",
    city: "San Antonio, TX",
    img: "https://images.pexels.com/photos/33747711/pexels-photo-33747711.png?auto=compress&cs=tinysrgb&w=1400",
    aspect: "wide",
  },
  {
    id: "p3",
    service: "Mulch Refresh",
    city: "Boerne, TX",
    img: "https://images.pexels.com/photos/4839348/pexels-photo-4839348.jpeg?auto=compress&cs=tinysrgb&w=1400",
    aspect: "square",
  },
  {
    id: "p4",
    service: "Spring Clean-Up",
    city: "Fair Oaks Ranch, TX",
    img: "https://images.pexels.com/photos/7587882/pexels-photo-7587882.jpeg?auto=compress&cs=tinysrgb&w=1400",
    aspect: "square",
  },
  {
    id: "p5",
    service: "Flower Bed Reset",
    city: "Schertz, TX",
    img: "https://images.pexels.com/photos/29162607/pexels-photo-29162607.jpeg?auto=compress&cs=tinysrgb&w=1400",
    aspect: "wide",
  },
  {
    id: "p6",
    service: "Brush Trim",
    city: "Alamo Heights, TX",
    img: "https://images.pexels.com/photos/7598365/pexels-photo-7598365.jpeg?auto=compress&cs=tinysrgb&w=1400",
    aspect: "tall",
  },
  {
    id: "p7",
    service: "Landscape & Pavers",
    city: "San Antonio Metro, TX",
    img: "https://images.pexels.com/photos/9976121/pexels-photo-9976121.jpeg?auto=compress&cs=tinysrgb&w=1400",
    aspect: "wide",
  },
];

const HEIGHT_BY_ASPECT = {
  tall: "h-[520px] md:h-[600px]",
  wide: "h-[320px] md:h-[360px]",
  square: "h-[400px] md:h-[440px]",
};

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="work"
      data-testid="gallery-section"
      className="relative py-24 md:py-32 bg-background overflow-hidden"
    >
      {/* Backdrop accents */}
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container-ic relative">
        {/* Header — asymmetric */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14 md:mb-20">
          <div className="max-w-2xl">
            <div className="overline mb-5" data-testid="gallery-overline">
              <Scissors className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />
              Recent Work
            </div>
            <h2
              className="font-display text-white text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.95]"
              data-testid="gallery-heading"
            >
              Curb appeal,
              <br />
              <span className="text-primary">crafted</span> weekly.
            </h2>
          </div>
          <div className="md:max-w-sm md:text-right">
            <p className="text-muted-foreground leading-relaxed">
              A look at recent jobs across Austin and the surrounding 'burbs. Yours
              could be next.
            </p>
            <a
              href="#booking"
              className="inline-flex items-center gap-2 mt-5 text-sm uppercase tracking-widest text-primary hover:text-white transition-colors group"
              data-testid="gallery-cta-book"
            >
              Get on the route
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>

        {/* Asymmetric mosaic */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {PROJECTS.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setActive(p)}
              className={`group relative overflow-hidden bg-black/60 border border-border hover:border-primary/60 transition-colors cursor-pointer text-left ${
                HEIGHT_BY_ASPECT[p.aspect]
              } ${i === 0 ? "md:row-span-2" : ""} ${i === 5 ? "md:row-span-2" : ""}`}
              data-testid={`gallery-tile-${p.id}`}
            >
              <img
                src={p.img}
                alt={`${p.service} in ${p.city}`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/15 transition-colors duration-500 mix-blend-overlay" />

              {/* Corner index */}
              <div className="absolute top-4 left-4 font-display text-xs uppercase tracking-[0.3em] text-primary/80">
                {String(i + 1).padStart(2, "0")} / {String(PROJECTS.length).padStart(2, "0")}
              </div>

              {/* Bottom meta */}
              <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                <div className="h-px w-8 bg-primary mb-3 transition-all duration-500 group-hover:w-16" />
                <div
                  className="font-display text-white text-xl md:text-2xl font-bold uppercase tracking-tight leading-tight"
                  data-testid={`gallery-service-${p.id}`}
                >
                  {p.service}
                </div>
                <div className="mt-1 flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/60">
                  <MapPin className="h-3 w-3 text-primary" />
                  {p.city}
                </div>
              </div>

              {/* Hover badge */}
              <div className="absolute top-4 right-4 w-9 h-9 border border-primary/60 bg-black/60 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 -translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                <ArrowUpRight className="h-4 w-4 text-primary" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10 animate-in fade-in"
          onClick={() => setActive(null)}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={() => setActive(null)}
            className="absolute top-5 right-5 w-11 h-11 border border-primary/60 bg-black/80 text-primary hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center"
            aria-label="Close"
            data-testid="gallery-lightbox-close"
          >
            <X className="h-5 w-5" />
          </button>
          <div
            className="relative max-w-5xl w-full max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={active.img}
              alt={`${active.service} in ${active.city}`}
              className="w-full h-full object-contain max-h-[80vh]"
            />
            <div className="mt-4 flex items-center justify-between">
              <div>
                <div className="font-display text-white text-2xl md:text-3xl font-bold uppercase tracking-tight">
                  {active.service}
                </div>
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-primary mt-1">
                  <MapPin className="h-3 w-3" />
                  {active.city}
                </div>
              </div>
              <a
                href="#booking"
                onClick={() => setActive(null)}
                className="btn-primary"
                data-testid="gallery-lightbox-cta"
              >
                Book a Job Like This
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
